import React, { type FunctionComponent, Fragment, useCallback, useMemo } from 'react'
import {
  PageLayout,
  PageLayoutHeader,
  PageLayoutFooter,
  PageLayoutContent,
  spacing,
} from '@retailer-platform/shared-components'
import { useParams } from 'react-router'
import { Breadcrumb } from '@retailer-platform/shared-components'
import { type DashRouteComponentProps } from 'legacy/dash-components/dash-route/utils/dashRoute.types'
import DocumentTitle from 'legacy/components/DocumentTitle'
import { RetailerCollectionsForm } from 'domains/retailer-collections/components/form/RetailerCollectionsForm'
import { useDomainMessages } from 'domains/retailer-collections/utils/domain/intl'
import { RetailerCollectionsFormFooter } from 'domains/retailer-collections/components/form/components/footer/RetailerCollectionsFormFooter'
import {
  RetailerCollectionContentType,
  useGetInstacartCollectionQuery,
  useUpdateInstacartCollectionMutation,
} from 'domains/retailer-collections/api'
import { type RetailerCollectionFormValues } from 'domains/retailer-collections/components/form/utils/types'
import { useRetailerId } from 'utils/routing/params.hooks'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { type RetailerCollectionsEditParams } from 'domains/retailer-collections/routing/routes'
import { useDomainGoToPath } from 'domains/retailer-collections/utils/domain/routing'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import { type SupportedSurface } from 'domains/retailer-collections/utils/types'
import { RetailerCollectionsContext } from 'domains/retailer-collections/utils/retailerCollections.context'
import { trackRetailerCollectionsEvent } from 'domains/retailer-collections/utils/eventTracking'

interface Props extends DashRouteComponentProps<{}> {}

const InstacartCollectionsEditPage: FunctionComponent<Props> = ({ match }) => {
  const retailerId = useRetailerId()
  const { notify, notifyError } = useNotifications()
  const { id } = useParams<RetailerCollectionsEditParams>()
  const goToCollectionsList = useDomainGoToPath('retailer-collections-list')

  // convert "c1000" to ["c", "1000"]
  const regex = new RegExp('([cs])([0-9]+)')
  const found = id.match(regex)
  const [icCollectionTypeChar, icCollectionId] = found.slice(1, 3)
  const contentType =
    icCollectionTypeChar == 'c'
      ? RetailerCollectionContentType.Collections
      : RetailerCollectionContentType.Products

  const { data, loading, error, refetch } = useGetInstacartCollectionQuery({
    variables: {
      id: icCollectionId,
      retailerId,
      contentType,
    },
    fetchPolicy: 'no-cache',
  })

  const messages = useDomainMessages({
    title: 'retailerCollectionsDomain.edit.title',
    success: 'retailerCollectionsDomain.edit.success',
    error: 'retailerCollectionsDomain.edit.error',
  })

  const [mutateUpdate, mutateUpdateResult] = useUpdateInstacartCollectionMutation()

  const onSubmit = useCallback(
    ({
      effectiveDates: { startDate, endDate },
      translations,
      ...formData
    }: RetailerCollectionFormValues) => {
      trackRetailerCollectionsEvent('collectionEditPageSubmit', { retailerId })
      mutateUpdate({
        variables: {
          id: icCollectionId,
          retailerId,
          ...formData,
        },
      })
    },
    [icCollectionId, mutateUpdate, retailerId]
  )

  React.useEffect(() => {
    if (mutateUpdateResult.error) {
      notifyError(messages.error, 'modern')
      trackRetailerCollectionsEvent('collectionEditPageFailure', { retailerId })
    }

    if (mutateUpdateResult.data) {
      notify(messages.success, 'modern')
      trackRetailerCollectionsEvent('collectionEditPageSuccess', { retailerId })
      // for now kick out to main page since we don't have a great way to determine the new route
      goToCollectionsList()
    }
  }, [mutateUpdateResult, messages, notify, notifyError, refetch, goToCollectionsList, retailerId])

  const initialFormState = useMemo<RetailerCollectionFormValues>(() => {
    const instacartCollectionData =
      data?.retailerCollectionsServiceGetInstacartCollection?.instacartCollection

    if (!instacartCollectionData) return

    const {
      name,
      slug,
      startDate,
      endDate,
      translations,
      tileImageUrl,
      contentType,
      status,
      surfaces,
      storeConfigurations,
      position,
      parentId,
    } = instacartCollectionData

    return {
      status,
      parentId,
      contentType,
      effectiveDates: { startDate, endDate },
      surfaces: surfaces as SupportedSurface[],
      // Remove __typename from all objects
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      storeConfigurations: storeConfigurations.map(({ __typename, ...rest }) => rest),
      name,
      slug,
      tileImageUrl,
      translations,
      position,
    }
  }, [data?.retailerCollectionsServiceGetInstacartCollection?.instacartCollection])

  if (loading) return <LoadingPage />

  if (error)
    return (
      <ErrorPage
        action={() => {
          refetch()
        }}
      />
    )

  return (
    <Fragment>
      <DocumentTitle title={messages.title} />

      <PageLayout noSidebar>
        <PageLayoutHeader css={{ paddingLeft: spacing.X20 }}>
          <Breadcrumb>
            {messages.title}: {initialFormState.name}
          </Breadcrumb>
        </PageLayoutHeader>

        <PageLayoutContent>
          <RetailerCollectionsForm
            mode="edit"
            collectionType="instacart"
            onSubmit={onSubmit}
            loading={loading}
            passedFormState={initialFormState}
          />
        </PageLayoutContent>

        <PageLayoutFooter css={{ justifyContent: 'flex-end' }}>
          <RetailerCollectionsFormFooter />
        </PageLayoutFooter>
      </PageLayout>
    </Fragment>
  )
}

export default props => (
  <RetailerCollectionsContext>
    <InstacartCollectionsEditPage {...props} />
  </RetailerCollectionsContext>
)
